import { formatCPF } from '@brazilian-utils/brazilian-utils';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
  CircularProgress,
} from '@mui/material';
import moment from 'moment';
import { useState, useMemo } from 'react';

export interface ColumnDefinition {
  field: string;
  headerName: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  width?: number;
}

interface DynamicDataGridProps {
  data: Record<string, any>[];
  columns?: ColumnDefinition[];
  title?: string;
  loading?: boolean;
  emptyMessage?: string;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onRowClick?: (record: Record<string, any>) => void;
}

/**
 * Infers column definitions from data if not provided
 */
const inferColumnsFromData = (data: Record<string, any>[]): ColumnDefinition[] => {
  if (!data || data.length === 0) return [];

  const firstRow = data[0];
  const columns: ColumnDefinition[] = [];

  for (const [key, value] of Object.entries(firstRow)) {
    const column: ColumnDefinition = {
      field: key,
      headerName: key,
      type: 'string',
    };

    if (value === null || value === undefined) {
      column.type = 'string';
    } else if (typeof value === 'number') {
      column.type = 'number';
    } else if (typeof value === 'boolean') {
      column.type = 'boolean';
    } else if (value instanceof Date) {
      column.type = 'date';
    } else if (typeof value === 'string') {
      // Check if it looks like a date
      if (/^\d{4}-\d{2}-\d{2}/.test(value) || /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
        column.type = 'date';
      }
      // Check if it looks like a number
      else if (/^-?\d+\.?\d*$/.test(value) && !isNaN(parseFloat(value))) {
        column.type = 'number';
      }
    }

    columns.push(column);
  }

  return columns;
};

/**
 * Checks if field is volume/liters related
 */
const isVolumeField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('litro') ||
    lowerField.includes('liter') ||
    lowerField.includes('volume') ||
    lowerField === 'amount' ||
    lowerField.includes('_amount') ||
    lowerField.includes('amount_') ||
    lowerField === 'quantidade' ||
    lowerField.includes('total_amount') ||
    lowerField.includes('average_amount')
  );
};

/**
 * Checks if field is a unit price (price per liter)
 */
const isUnitPriceField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('price_per_liter') ||
    lowerField.includes('preco_por_litro') ||
    lowerField.includes('unit_price') ||
    lowerField.includes('preco_unitario') ||
    lowerField.includes('old_price_per_liter') ||
    lowerField.includes('average_price')
  );
};

/**
 * Checks if field is points related (not currency)
 */
const isPointsField = (field: string): boolean => {
  const lowerField = field.toLowerCase();
  return (
    lowerField.includes('ponto') ||
    lowerField.includes('point') ||
    lowerField.includes('score') ||
    lowerField.includes('saldo') ||
    lowerField.includes('balance')
  );
};

/**
 * Checks if field is currency/money related (excluding volume and points fields)
 */
const isCurrencyField = (field: string): boolean => {
  const lowerField = field.toLowerCase();

  // Exclude volume-related fields
  if (isVolumeField(field)) return false;

  // Exclude points-related fields
  if (isPointsField(field)) return false;

  return (
    lowerField.includes('valor') ||
    lowerField.includes('value') ||
    lowerField.includes('price') ||
    lowerField.includes('total') ||
    lowerField.includes('preco') ||
    lowerField.includes('desconto') ||
    lowerField.includes('discount') ||
    lowerField.includes('faturamento') ||
    lowerField.includes('ticket')
  );
};

/**
 * Formats a value based on its type and field name
 */
const formatValue = (value: any, type: string, field: string): string => {
  if (value === null || value === undefined) return '-';

  // Special field handling
  const lowerField = field.toLowerCase();
  if (lowerField === 'cpf' || lowerField.includes('cpf')) {
    try {
      return formatCPF(String(value));
    } catch {
      return String(value);
    }
  }

  switch (type) {
    case 'number':
      if (typeof value === 'number') {
        // Check if it's a volume/liters field
        if (isVolumeField(field)) {
          return value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + ' L';
        }

        // Check if it's a unit price field (3 decimal places)
        if (isUnitPriceField(field)) {
          return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          });
        }

        // Check if it's a points field
        if (isPointsField(field)) {
          return value.toLocaleString('pt-BR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }) + ' pts';
        }

        // Check if it's a currency field
        if (isCurrencyField(field)) {
          return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }

        // Default number formatting
        return value.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      }
      return String(value);
    case 'date':
      try {
        const date = moment(value);
        if (date.isValid()) {
          // Include time if present
          if (date.hour() !== 0 || date.minute() !== 0 || date.second() !== 0) {
            return date.format('DD/MM/YYYY HH:mm');
          }
          return date.format('DD/MM/YYYY');
        }
        return String(value);
      } catch {
        return String(value);
      }
    case 'boolean':
      return value ? 'Sim' : 'Não';
    default:
      return String(value);
  }
};

/**
 * DynamicDataGrid component that automatically infers columns from data
 * Can be used standalone or with React Admin
 */
const DynamicDataGrid = ({
  data,
  columns: providedColumns,
  title,
  loading = false,
  emptyMessage = 'Nenhum registro encontrado',
  defaultRowsPerPage = 25,
  rowsPerPageOptions = [10, 25, 50, 100],
  onRowClick,
}: DynamicDataGridProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // Infer columns if not provided
  const columns = useMemo(() => {
    return providedColumns || inferColumnsFromData(data);
  }, [providedColumns, data]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!orderBy || !data) return data;

    const sortColumn = columns.find((c) => c.field === orderBy);

    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (sortColumn?.type === 'number') {
        comparison = Number(aValue) - Number(bValue);
      } else {
        comparison = String(aValue).localeCompare(String(bValue), 'pt-BR');
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [data, orderBy, order]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!sortedData) return [];
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleSort = (field: string) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden' }}>
      {title && (
        <Typography variant="h6" sx={{ p: 2, fontWeight: 500 }}>
          {title}
        </Typography>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  sx={{
                    fontWeight: 600,
                    bgcolor: '#7869DF',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    minWidth: column.width || 100,
                  }}
                  sortDirection={orderBy === column.field ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.field}
                    direction={orderBy === column.field ? order : 'asc'}
                    onClick={() => handleSort(column.field)}
                    sx={{
                      color: '#fff !important',
                      '&.Mui-active': {
                        color: '#fff !important',
                      },
                      '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                      },
                    }}
                  >
                    {column.headerName}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': { bgcolor: '#f9f9f9' },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    sx={{
                      whiteSpace: 'nowrap',
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {formatValue(row[column.field], column.type, column.field)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  );
};

export default DynamicDataGrid;
